import React, { useState } from 'react';
import { Moment } from 'moment';

// hooks
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

// MUI components
import { makeStyles } from '@material-ui/core/styles';
import { Grid, CircularProgress, Typography } from '@material-ui/core';
import { Delete } from '@material-ui/icons';

// components
import Message from './Message';
import ScheduleMessageDialog from './ScheduleMessageDialog';
import DazzlingDialog from 'components/DazzlingDialog';

// action creators
import {
  updateMessageAction,
  deleteMessageAction,
} from 'store/actions/messageActions';

// selectors
import getMessagesForThread from 'store/selectors/getMessagesForThread';
import getLoadingFromState from 'store/selectors/getLoadingFromState';
import getCurrentGroupId from 'store/selectors/getCurrentGroupId';

function Thread({ id }: { id?: number }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { search } = useLocation();
  const threadId = id || new URLSearchParams(search).get('thread');
  const { messages, messagesLoading, currentGroupId } = useSelector(state => ({
    messages: getMessagesForThread(threadId)(state) as any,
    messagesLoading: getLoadingFromState('message', null, false)(state),
    currentGroupId: getCurrentGroupId(state),
  }));

  const [selectedMessage, setSelectedMessage] = useState<TypedMap<
    Message
  > | null>(null);
  const [activeDialog, setActiveDialog] = useState<'update' | 'delete' | null>(
    null
  );

  const openDialogFor = (dialog: 'update' | 'delete') => (
    message: TypedMap<Message>
  ) => {
    setSelectedMessage(message);
    setActiveDialog(dialog);
  };
  const handleCloseDialog = () => setActiveDialog(null);

  const handleUpdate = (date: Moment, body: string) => {
    if (!selectedMessage) return;
    dispatch(
      updateMessageAction({
        id: selectedMessage.get('id'),
        groupId: currentGroupId,
        body,
        scheduledAt: date.toISOString(),
      })
    );
  };
  const handleDelete = () => {
    if (!selectedMessage) return;
    dispatch(
      deleteMessageAction({
        id: selectedMessage.get('id'),
        groupId: currentGroupId,
      })
    );
  };

  return (
    <Grid container direction='column' spacing={1}>
      {messagesLoading && !messages.size ? (
        <Grid item xs className={classes.loadingIndicator}>
          <CircularProgress size={30} />
        </Grid>
      ) : (
        messages.map((message: TypedMap<Message>) => (
          <Grid
            item
            xs={10}
            sm={8}
            md={6}
            key={message.get('id')}
            className={
              message.get('type') === 'outgoing'
                ? classes.alignRight
                : classes.alignLeft
            }
          >
            <Message
              message={message}
              onUpdate={openDialogFor('update')}
              onDelete={openDialogFor('delete')}
            />
          </Grid>
        ))
      )}

      <ScheduleMessageDialog
        open={activeDialog === 'update'}
        onClose={handleCloseDialog}
        onSchedule={handleUpdate}
        isEditing
        body={selectedMessage?.get('body')}
        scheduledAt={selectedMessage?.get('scheduledAt')}
      />
      <DazzlingDialog
        id='deleteScheduledMessageDialog'
        open={activeDialog === 'delete'}
        maxWidth='sm'
        handleClose={handleCloseDialog}
        acceptLabel='Delete'
        onAccept={handleDelete}
        slice='message'
        metaId='delete'
        successMessage='Scheduled message deleted successfully!'
        failureMessage='Scheduled message failed to delete'
        headerProps={{
          icon: Delete,
          title: 'Delete scheduled message?',
        }}
      >
        <Typography>This message won't be sent.</Typography>
      </DazzlingDialog>
    </Grid>
  );
}

const useStyles = makeStyles(theme => {
  return {
    alignRight: {
      alignSelf: 'flex-end',
    },
    alignLeft: {
      alignSelf: 'flex-start',
    },
    loadingIndicator: {
      alignSelf: 'center',
    },
  };
});

export default Thread;
